import moment from "moment";

export const generatePrescriptionHTML = (activeData, patient, settings) => {
    // Calculate age
    const calculateAgeForPDF = (dob) => {
        if (!dob) return "";
        const birthDate = new Date(dob);
        const today = new Date();
        let years = today.getFullYear() - birthDate.getFullYear();
        let months = today.getMonth() - birthDate.getMonth();
        if (
            months < 0 ||
            (months === 0 && today.getDate() < birthDate.getDate())
        ) {
            years--;
            months += 12;
        }
        if (years > 0) return `${years} Yrs`;
        if (months > 0) return `${months} M`;
        return "";
    };

    const ageDisplay = calculateAgeForPDF(patient?.dob);
    const visitDate = moment().format("MMMM D, YYYY");
    const opdNumber = activeData?.opd_number || "N/A";

    // Professional styling - Patient info BOLD as requested
    let html = `
    <div style="font-family: 'Inter', sans-serif; font-size: 12px; color: #1f2937; line-height: 1.4;">
      <!-- Patient Info - Bold, with divider -->
      <table style="width: 100%; margin-bottom: 4px; border-collapse: collapse; border-bottom: 1px solid #d1d5db;">
        <tr>
          <td style="width: 50%; vertical-align: bottom; padding: 4px 0;">
            <span style="font-size: 12px; color: #374151; font-weight: bold; font-family: 'Inter', sans-serif;">${patient?.full_name || ""}</span>
            <span style="font-size: 12px; color: #6b7280; font-weight: bold; margin-left: 8px; font-family: 'Inter', sans-serif;">${ageDisplay}</span>
            <span style="font-size: 12px; color: #6b7280; font-weight: bold; margin-left: 8px; font-family: 'Inter', sans-serif;">${patient?.gender || ""}</span>
            <span style="font-size: 12px; color: #6b7280; font-weight: bold; margin-left: 8px; font-family: 'Inter', sans-serif;">${patient?.phone || ""}</span>
          </td>
          <td style="width: 50%; text-align: right; vertical-align: bottom; padding: 4px 0; white-space: nowrap;">
            <span style="font-size: 12px; color: #374151; font-weight: bold; font-style: italic; font-family: 'Inter', sans-serif;">Visit Date: ${visitDate}</span>
            <span style="font-size: 12px; color: #6b7280; font-weight: bold; font-style: italic; margin-left: 12px; font-family: 'Inter', sans-serif;">OPD No: <i>${opdNumber}</i></span>
          </td>
        </tr>
      </table>
    `;

    // Section rows with italic labels AND border-bottom dividers
    const addSection = (title, content, widthPercent = 14) => {
        if (!content || content === "") return "";
        return `
      <table style="width: 100%; margin-bottom: 4px; border-collapse: collapse; border-bottom: 1px solid #d1d5db;">
        <tr>
          <td style="width: ${widthPercent}%; font-style: italic; color: #4b5563; vertical-align: top; padding: 4px 0; font-size: 12px; font-family: 'Inter', sans-serif; font-weight: 600;">${title}</td>
          <td style="width: ${100 - widthPercent}%; vertical-align: top; padding: 4px 0; padding-left: 10px; font-size: 12px; color: #1f2937; font-family: 'Inter', sans-serif;">${content}</td>
        </tr>
      </table>
      `;
    };

    // Filter tags by type
    const allTags = activeData?.qp_cm_consultation_tags || [];
    const getTagsByType = (tagTypeIds) => {
        if (!Array.isArray(tagTypeIds)) tagTypeIds = [tagTypeIds];
        return allTags
            .filter((t) => {
                if (!t) return false;
                // Robust check: Ensure tag_type is treated as array
                const types = Array.isArray(t.tag_type)
                    ? t.tag_type
                    : t.tag_type
                      ? [t.tag_type]
                      : [];
                // Use loose equality (==) to handle string vs number mismatch
                return types.some((id) => tagTypeIds.some((tid) => tid == id));
            })
            .map((t) => t?.name)
            .filter(Boolean)
            .join(", ");
    };

    // Process sections based on pageOrdering
    const pageOrdering = settings?.pageOrdering || [];

    pageOrdering.forEach((section) => {
        const key = section?.key;
        const label = section?.label;
        const tagId = section?.tagId;
        const offset = section?.offset;

        switch (key) {
            case "customField":
                if (offset === "TAGS" && tagId) {
                    const tags = getTagsByType([tagId]);
                    if (tags) {
                        html += addSection(label, tags, 14);
                    }
                } else if (offset === "ADDICTIONS") {
                    const addictions = patient?.addictions || [];
                    if (addictions.length > 0) {
                        const addStr = addictions
                            .map((a) => a?.name)
                            .filter(Boolean)
                            .join(", ");
                        html += addSection("Addictions", addStr, 14);
                    }
                } else if (offset === "ALLERGY") {
                    const allergies = patient?.allergy || [];
                    if (allergies.length > 0) {
                        const allStr = allergies
                            .map((a) => a?.name)
                            .filter(Boolean)
                            .join(", ");
                        html += addSection("Allergies", allStr, 14);
                    }
                } else if (offset === "PATIENT_HISTORY") {
                    const history = patient?.medical_history || [];
                    if (history.length > 0) {
                        const histStr = history
                            .map((h) => h?.name)
                            .filter(Boolean)
                            .join(", ");
                        html += addSection("Patient History", histStr, 14);
                    }
                }
                break;

            case "vitals":
                const vitals = activeData?.qp_cm_vitals || [];
                const validVitals = vitals.filter((v) => {
                    const v1 = parseFloat(v?.value1);
                    return !isNaN(v1) && v1 !== 0;
                });

                if (validVitals.length > 0) {
                    // Match reference image: horizontal layout with label above value
                    let vitalsContent = '<table style="width: auto;"><tr>';
                    validVitals.forEach((v) => {
                        // Format like reference: "Weight" (not WEIGHT), "55 kg" (not 55.00 kg)
                        const label = v?.type?.label || "";
                        // Remove .00 from values
                        const val1 = parseFloat(v?.value1 || 0);
                        const val2 = parseFloat(v?.value2 || 0);
                        let displayVal = val1.toString();
                        // For BP, show both values with separator
                        if (label.toLowerCase() === "bp" && val2 > 0) {
                            displayVal = `${val1}/${val2}`;
                        }
                        const unit = v?.selected_unit || "";

                        vitalsContent += `
                <td style="padding-right: 40px; vertical-align: top;">
                  <div style="font-size: 11px; color: #6b7280; text-transform: capitalize; font-family: 'Inter', sans-serif; margin-bottom: 2px;">${label}</div>
                  <div style="font-size: 12px; color: #1f2937; font-family: 'Inter', sans-serif; white-space: nowrap;">${displayVal} ${unit}</div>
              `;

                        // Handle extra BP readings
                        if (
                            label.toLowerCase() === "bp" &&
                            v?.reading &&
                            v.reading.length > 0
                        ) {
                            v.reading.forEach((r) => {
                                const rVal1 = r?.value1;
                                const rVal2 = r?.value2;
                                const rUnit = r?.valueUnit || "";
                                const rTime = r.timeStamp
                                    ? moment(r.timeStamp).format("hh:mm A")
                                    : r.interval
                                      ? `${r.interval} ${r.intervalUnit || "min"}`
                                      : "";

                                let rDisplay = `${rVal1}`;
                                if (rVal2) rDisplay += `/${rVal2}`;

                                vitalsContent += `
                     <div style="font-size: 10px; color: #6b7280; margin-top: 2px; font-family: 'Inter', sans-serif; white-space: nowrap;">
                       ${rDisplay} ${rUnit} <span style="font-size: 9px;">(${rTime})</span>
                     </div>
                   `;
                            });
                        }

                        vitalsContent += `</td>`;
                    });
                    vitalsContent += "</tr></table>";
                    html += addSection("Vitals", vitalsContent, 14);
                }
                break;

            case "medicineTable":
                const medicines = activeData?.medicine_data || [];
                if (medicines.length > 0) {
                    let medRows = "";
                    medicines.forEach((med, idx) => {
                        const name = med?.medicineName || "";
                        const generic = med?.genericName?.name || "";
                        const dose = med?.dosage?.name || "";
                        const unit = med?.unit?.name || "";
                        const when = med?.when?.name || "";
                        const duration = med?.duration || "";
                        const freq = med?.frequency?.name || "";
                        const notes = med?.notes?.name || "";

                        // Match reference: "1-1-1 MG (AFTER FOOD)" - dose+unit together, when in parens
                        const doseDisplay = `${dose} ${unit}`.trim();

                        medRows += `
                <tr>
                  <td style="padding: 4px 8px; color: #6b7280; vertical-align: top; font-size: 11px; font-family: 'Inter', sans-serif;">${idx + 1}</td>
                  <td style="padding: 4px 8px; vertical-align: top; font-family: 'Inter', sans-serif;">
                    <div style="font-weight: 600; color: #111827; font-size: 11px; text-transform: uppercase;">${name}</div>
                    ${generic ? `<div style="font-size: 10px; color: #6b7280; margin-top: 1px; font-style: italic;">${generic}</div>` : ""}
                    ${notes ? `<div style="font-size: 10px; color: #6b7280; margin-top: 4px;">Remark: ${notes}</div>` : ""}
                  </td>
                  <td style="padding: 4px 8px; vertical-align: top; font-family: 'Inter', sans-serif;">
                    <div style="color: #1f2937; font-size: 11px;">${doseDisplay} <span style="color: #6b7280;">(${when})</span></div>
                  </td>
                  <td style="padding: 4px 8px; vertical-align: top; font-family: 'Inter', sans-serif;">
                    <div style="color: #1f2937; font-size: 11px;">${duration} <span style="color: #6b7280;">(${freq})</span></div>
                  </td>
                </tr>
              `;
                    });

                    html += `
              <div style="margin: 8px 0;">
                <div style="margin-bottom: 4px; color: #111827;">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" style="height: 15px; width: 12px; fill: currentColor; vertical-align: middle;">
                    <path d="M301.26 352l78.06-78.06c6.25-6.25 6.25-16.38 0-22.63l-22.63-22.63c-6.25-6.25-16.38-6.25-22.63 0L256 306.74l-83.96-83.96C219.31 216.8 256 176.89 256 128c0-53.02-42.98-96-96-96H16C7.16 32 0 39.16 0 48v256c0 8.84 7.16 16 16 16h32c8.84 0 16-7.16 16-16v-80h18.75l128 128-78.06 78.06c-6.25 6.25-6.25 16.38 0 22.63l22.63 22.63c6.25 6.25 16.38 6.25 22.63 0L256 397.25l78.06 78.06c6.25 6.25 16.38 6.25 22.63 0l22.63-22.63c6.25-6.25 6.25-16.38 0-22.63L301.26 352zM64 96h96c17.64 0 32 14.36 32 32s-14.36 32-32 32H64V96z"/>
                  </svg>
                </div>
                <table style="width: 100%; border-collapse: collapse; font-size: 11px; font-family: 'Inter', sans-serif;">
                  <thead>
                    <tr style="background-color: #f3f4f6;">
                      <th style="width: 5%; padding: 4px 8px; text-align: left; font-weight: 600; color: #374151; font-size: 10px;">NO</th>
                      <th style="width: 45%; padding: 4px 8px; text-align: left; font-weight: 600; color: #374151; font-size: 10px;">MEDICINE & DETAILS</th>
                      <th style="width: 30%; padding: 4px 8px; text-align: left; font-weight: 600; color: #374151; font-size: 10px;">
                        DOSE
                        <div style="font-weight: 400; font-style: italic; color: #6b7280; font-size: 9px; margin-top: 2px;">Morning - Afternoon - Night</div>
                      </th>
                      <th style="width: 20%; padding: 4px 8px; text-align: left; font-weight: 600; color: #374151; font-size: 10px;">DURATION</th>
                    </tr>
                  </thead>
                  <tbody>${medRows}</tbody>
                </table>
              </div>
            `;
                }
                break;

            case "adviceDirection":
                const advice = activeData?.new_advice_and_direction || "";
                if (advice) {
                    // Match reference image: bullet points with styling matching client
                    const formattedAdvice = advice
                        .split("\n")
                        .filter((l) => l.trim())
                        .map((l) => `• ${l.trim()}`)
                        .join("<br>");
                    // Use italic and gray for advice content to match client side
                    const styledAdvice = `<div style="font-style: italic; color: #4b5563;">${formattedAdvice}</div>`;
                    html += addSection("Advice", styledAdvice, 14);
                }
                break;

            case "nextVisit":
                const nextVisit = activeData?.next_visit_date;
                if (nextVisit) {
                    const formattedDate =
                        moment(nextVisit).format("MMMM D, YYYY");
                    html += addSection("Next visit Date", formattedDate, 16);
                }
                break;

            case "observation":
                const observation = activeData?.observation;
                if (observation) {
                    html += addSection("Observation", observation, 16);
                }
                break;

            case "pathLab":
                const labTests = activeData?.lab_test_data || [];
                if (labTests.length > 0) {
                    let labRows = "";
                    labTests.forEach((test, idx) => {
                        labRows += `
                <tr>
                  <td style="padding: 4px 4px;">${idx + 1}</td>
                  <td style="padding: 4px 4px; font-weight: 600;">${test?.testName || ""}</td>
                  <td style="padding: 4px 4px;">${test?.unit || ""}</td>
                  <td style="padding: 4px 4px;">${test?.value || ""}</td>
                  <td style="padding: 4px 4px;">${test?.status || ""}</td>
                </tr>
              `;
                    });
                    html += `
              <div style="margin: 8px 0;">
                <div style="font-weight: 600; font-style: italic; color: #4b5563; margin-bottom: 4px;">Path & Lab Test</div>
                <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
                  <thead style="background-color: #f9fafb;">
                    <tr>
                      <th style="padding: 4px 4px; text-align: left;">No</th>
                      <th style="padding: 4px 4px; text-align: left;">Test Name</th>
                      <th style="padding: 4px 4px; text-align: left;">Unit</th>
                      <th style="padding: 4px 4px; text-align: left;">Value</th>
                      <th style="padding: 4px 4px; text-align: left;">Status</th>
                    </tr>
                  </thead>
                  <tbody>${labRows}</tbody>
                </table>
              </div>
            `;
                }
                break;

            case "referToSpecialist":
                const referral = activeData?.refferal_doctor;
                if (referral) {
                    const refName = referral?.name || "";
                    const refSpec = referral?.specialty || "";
                    const refClinic = referral?.clinic_name || "";
                    let refContent = `<strong>${refName}</strong>`;
                    if (refSpec) refContent += ` (${refSpec})`;
                    if (refClinic) refContent += `<br>${refClinic}`;
                    html += addSection("Referred To", refContent, 14);
                }
                break;
        }
    });

    html += "</div>";
    return html;
};
